import { FC } from 'react';
import { navigate } from 'gatsby';
import classNames from 'classnames';
import * as Yup from 'yup';
import isEqual from 'lodash/isEqual';
import { FormikHelpers } from 'formik/dist/types';
import { ButtonPrimary, Form, InputPhone, InputText, useSnackbar } from '@lambdacurry/component-library';
import { Exact, MeQuery, SetPasswordInput, UpdateAddressInput, UpdateUserInput } from 'lib/home/generated/types/api';
import { useUpdateUserProfileMutation } from 'lib/home/generated/queries';
import {
  addressValidation,
  confirmPasswordValidation,
  emailAddressValidation,
  nameValidation,
  phoneValidation
} from 'lib/forms/validation';
import { Card, CardFooter, CardHeader, CardSection } from 'lib/components';
import { AddressInputGroup, ConfirmPasswordInputGroup, DefaultLayout, NameInputGroup } from '../../../components';
import {
  EditProfileFormValues,
  isAddressUpdated,
  ProfileEditSection,
  isBasicInfoUpdated,
  isPasswordUpdated
} from '../../../components/Account/Edit/account-edit.helpers';
import { selectDefaultAddressFromMe } from '../../../util/helpers';

const EditAccountPage: FC = () => {
  const { mutate, isLoading } = useUpdateUserProfileMutation();
  const { addSnackbar } = useSnackbar();

  const validationSchema = Yup.object({
    ...nameValidation,
    ...phoneValidation,
    ...emailAddressValidation,
    address: Yup.object(addressValidation),
    passwords: Yup.object({
      current_password: Yup.string(),
      new_password: Yup.string(),
      confirm_password: confirmPasswordValidation.confirm_password
    })
  });

  const handleSubmit: (
    values: EditProfileFormValues,
    formikHelpers: FormikHelpers<EditProfileFormValues>,
    me: MeQuery['me']
  ) => void = (values, formikHelpers, me) => {
    const defaultAddress = selectDefaultAddressFromMe(me);

    const mutationData: Exact<{
      userInput: UpdateUserInput;
      updateAddressInput: UpdateAddressInput;
      setPasswordInput: SetPasswordInput;
    }> = {
      userInput: {},
      updateAddressInput: { id: defaultAddress.id },
      setPasswordInput: {}
    };

    if (isAddressUpdated(values, defaultAddress))
      mutationData.updateAddressInput = { ...defaultAddress, ...values.address };

    if (isBasicInfoUpdated(values, me))
      mutationData.userInput = {
        id: me.id,
        first_name: values.first_name,
        last_name: values.last_name,
        email: values.email,
        phone: values.phone
      };

    if (isPasswordUpdated(values))
      mutationData.setPasswordInput = {
        current_password: values.passwords.current_password,
        new_password: values.passwords.new_password
      };

    mutate(mutationData, {
      onSuccess: () => {
        // TODO: if response requires email address confirmation, we'll need a confirm email address step (https://lambdacurry.atlassian.net/browse/NHD-316)
        navigate('/account');
      },
      onError: () => {
        addSnackbar('There was an error updating your profile.', { variant: 'error' });
      }
    });
  };

  return (
    <DefaultLayout backTo="/account" className={classNames('account-edit')}>
      {({ me }: { me: MeQuery['me'] }) => {
        const defaultAddress = selectDefaultAddressFromMe(me);

        const initialValues = {
          first_name: me.first_name || '',
          last_name: me.last_name || '',
          email: me.email || '',
          phone: me.phone || '',
          address: {
            first_name: defaultAddress.first_name || me.first_name || '',
            last_name: defaultAddress.last_name || me.last_name || '',
            line1: defaultAddress.line1 || '',
            line2: defaultAddress.line2 || '',
            city: defaultAddress.city || '',
            state: defaultAddress.state || '',
            postal_code: defaultAddress.postal_code || '',
            country: 'US'
          },
          passwords: {
            current_password: '',
            new_password: '',
            confirm_password: ''
          }
        };

        return (
          <Form<EditProfileFormValues>
            validationSchema={validationSchema}
            initialValues={initialValues}
            onSubmit={(values, formikHelpers) => handleSubmit(values, formikHelpers, me)}
          >
            {formikProps => (
              <Card className="relative !overflow-visible">
                <CardHeader>
                  <CardSection className="!pb-0">
                    <h2 className="mb-6">Edit Profile</h2>
                    <hr />
                  </CardSection>
                </CardHeader>

                <CardSection>
                  <ProfileEditSection title="Basic Information">
                    <NameInputGroup />
                    <InputPhone name="phone" label="Phone Number" />
                    <InputText
                      name="email"
                      label="Email"
                      // TODO: add email validation step (https://lambdacurry.atlassian.net/browse/NHD-316)
                      // helperText={
                      //   formikProps.touched.email
                      //     ? 'For security reasons, you have to confirm your email if updated.'
                      //     : ''
                      // }
                    />
                  </ProfileEditSection>

                  <hr className="my-8" />

                  <ProfileEditSection title="Shipping Address">
                    <NameInputGroup name="address" />
                    <AddressInputGroup name="address" />
                  </ProfileEditSection>

                  <hr className="my-8" />

                  <ProfileEditSection title="Account Password">
                    <InputText name={`passwords.current_password`} label="Current Password" type="password" />
                    <ConfirmPasswordInputGroup name="passwords" label="New Password" />
                  </ProfileEditSection>
                </CardSection>

                <CardFooter className="z-10 !sticky bottom-0 !pt-0 !pb-4 md:!pt-0 md:!pb-8 bg-white !border-none">
                  <hr className="!mt-0 !mb-4 md:!mb-8" />
                  <div className="flex justify-end">
                    <ButtonPrimary
                      type="submit"
                      className="w-full md:w-48"
                      disabled={isEqual(initialValues, formikProps.values) || isLoading}
                    >
                      Update Info
                    </ButtonPrimary>
                  </div>
                </CardFooter>
              </Card>
            )}
          </Form>
        );
      }}
    </DefaultLayout>
  );
};

export default EditAccountPage;
